import React, {
  useCallback, useEffect, useRef, useState
}  from 'react';

import { scaleBand, scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { select } from 'd3-selection';

import Axis from '../axis/D3Axis';
import ChartGrid from '../axis/D3ChartGrid';
import ClipSection from '../utility/D3ClipPath';
import MouseoverTooltip from '../utility/D3MouseoverTooltip';

import Bars from './D3Bars';

const getAllXValues = (dataset = []) => dataset[0]?.data ? dataset[0].data.map(val => val.x) : []
const getAllYValues = (dataset = []) => dataset[0]?.data ? dataset[0].data.map(val => val.y) : []

const DEFAULT_MARGINS = {
  top: 20, right: 20, bottom: 100, left: 48
};

const BarChartAxis = ({
  chartId,
  xScale,
  yScale,
  xTicks,
  yTicks,
  xAxisLabel,
  yAxisLabel,
  margins = DEFAULT_MARGINS,
  height,
  width,
  clipPathId,
  labelRotation = -45,
}) => {
  const xSettings = {
    id: `${chartId}-x-axis`,
    scale: xScale,
    orient: 'bottom',
    transform: `translate(0, ${height})`,
    clipPath: `url(#${clipPathId})`,
    labelRotation,
    axisLabel: xAxisLabel,
    ticks: xTicks,
    height,
    width,
    margins,
  };
  const ySettings = {
    id: `${chartId}-y-axis`,
    scale: yScale,
    orient: 'left',
    transform: 'translate(0, 0)',
    axisLabel: yAxisLabel,
    ticks: yTicks,
    height,
    width,
    margins,
  };

  return (
    <g>
      <Axis {...xSettings} />
      <Axis {...ySettings} />
    </g>
  )
}

// TODO - No longer necessary to support multiple datasets in this component
export const D3BarChart = ({
  dataset,
  xTicks,
  yTicks,
  xAxisLabel,
  yAxisLabel,
  yAxisPadding = 1.2,
  margins = DEFAULT_MARGINS,
  chartId = 'bar-chart',
  labelRotation = -45,
  containerWidth = 800,
  containerHeight = 300,
  showGrid = true,
}) => {
  const allYValues = getAllYValues(dataset);
  const allXValues = getAllXValues(dataset);
  const clipPathId = `${chartId}-clip-path`;
  const [tooltipPoint, setTooltipPoint] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);
  const chartRef = useRef(null)

  const width = containerWidth > 0 ? containerWidth - margins.left - margins.right : 0;
  const height = containerHeight > 0 ? containerHeight - margins.top - margins.bottom : 0;

  const maxY = max(allYValues, val => val);

  // Consider basing padding and step sizing off of rounded square root / power of 2 values instead
  const paddedMaxY = ((Math.round(maxY / 10) * 10) || maxY) * yAxisPadding;

  const xScale = scaleBand()
    .domain(allXValues)
    .range([0, width])
    .padding(0.1);

  const yScale = scaleLinear()
    .domain([0, paddedMaxY])
    .range([height, 0])
    .nice();

  const onItemHover = useCallback((item, mousePointer) => {
    setTooltipPoint(item);

    if (mousePointer) {
      setMousePosition({ x: mousePointer[0], y: mousePointer[1] });
    } else {
      setMousePosition(null);
    }
  }, [])

  useEffect(() => {
    let svg = select(chartRef.current).select('svg')

    // On first render creates the svg
    if (svg.empty()) {
      svg = select(chartRef.current)
        .append('svg')
        .attr('width', width)
        .attr('height', height);
    }
  }, [height, width])

  return (
    <>
      <svg
        ref={chartRef}
        id={chartId}
        width={width + margins.left + margins.right}
        height={height + margins.top + margins.bottom}
      >
        <ClipSection
          id={clipPathId}
          x={0}
          y={0}
          height={height}
          width={width}
        />
        <g
          transform={`translate(${margins.left}, ${margins.top})`}
        >
          {
            showGrid && (
              <ChartGrid
                chartId={chartId}
                clipPathId={clipPathId}
                xScale={xScale}
                yScale={yScale}
                xTicks={xTicks}
                yTicks={yTicks}
                height={height}
                width={width}
                hideVertical
              />
            )
          }
          <BarChartAxis
            margins={margins}
            chartId={chartId}
            clipPathId={clipPathId}
            xAxisLabel={xAxisLabel}
            yAxisLabel={yAxisLabel}
            xScale={xScale}
            yScale={yScale}
            xTicks={xTicks}
            yTicks={yTicks}
            height={height}
            width={width}
            labelRotation={labelRotation}
          />
          <g clipPath={`url(#${clipPathId})`}>
            {
              dataset?.length && (
                dataset.map(set => (
                  <g key={`${chartId}-data-section-${set.id}`} id={`${chartId}-data-section-${set.id}`}>
                    <Bars
                      id={`${chartId}-bars`}
                      data={set.data}
                      barColor={set.barColor}
                      xScale={xScale}
                      yScale={yScale}
                      height={height}
                      onItemHover={onItemHover}
                    />
                  </g>
                ))
              )
            }
          </g>
        </g>
      </svg>
      <MouseoverTooltip
        id={`${chartId}-tooltip`}
        xScale={xScale}
        yScale={yScale}
        tooltipPoint={tooltipPoint}
        mousePosition={mousePosition}
        coordOffset={{ left: 64, top: -64 }}
        useMousePosition
      />
    </>
  )
};

export default D3BarChart;
